import { fabric } from 'fabric';
import { changeHeight } from '../utils/controlUtils';
import ControlBase from '../fabric/ControlBase';
import { type Resizable } from '../types';

export type ResizeControlOptions = {
  resizeDimension?: 'width' | 'height';
} & Partial<fabric.Control>;

/**
 * Resize along both axes. This control updates width and height directly
 */
class ResizeControl extends ControlBase {
  resizeDimension?: 'width' | 'height';

  constructor(options: ResizeControlOptions) {
    super(options);
    this.actionHandler = this.actionHandler.bind(this);
  }

  get resizeWidth() {
    return this.resizeDimension === 'width';
  }

  get resizeHeight() {
    return this.resizeDimension === 'height';
  }

  actionHandler(
    eventData: MouseEvent,
    transformData: fabric.Transform,
    x: number,
    y: number
  ): boolean {
    const both = !this.resizeWidth && !this.resizeHeight;
    let resized = false;
    const args = [eventData, transformData, x, y] as const;
    if (both || this.resizeWidth) {
      resized ||= this.resizeDimensionWithConstraint('width', ...args);
    }
    if (both || this.resizeHeight) {
      resized ||= this.resizeDimensionWithConstraint('height', ...args);
    }
    return resized;
  }

  resizeDimensionWithConstraint(
    dimension: 'width' | 'height',
    eventData: MouseEvent,
    transform: fabric.Transform,
    x: number,
    y: number
  ): boolean {
    const object = transform.target as fabric.Object & Resizable;
    const isWidth = dimension === 'width';
    const scale = isWidth ? object.scaleX : object.scaleY;

    const min = object.minDimension({ resizingWidth: isWidth });
    const max = object.maxDimension({ resizingWidth: isWidth });

    const isLeftTop =
      transform.originX === 'right' || transform.originY === 'bottom';

    const localPoint = fabric.controlsUtils.getLocalPoint(
      transform,
      transform.originX,
      transform.originY,
      x,
      y
    );

    let coordinate = (isWidth ? localPoint.x : localPoint.y) / scale;

    // When resizing from the left, avoid resizing when dragged past the origin
    if (isLeftTop) {
      coordinate *= -1;
    }

    const prevDimension = object[dimension];
    // Constrain object to min
    if (coordinate <= min) {
      // Avoid changing object position when resizing from left or top
      if (isLeftTop) {
        if (isWidth) {
          object.set('left', object.left + (object.width - min) * scale);
        } else {
          object.set('top', object.top + (object.height - min) * scale);
        }
      }
      object.set(dimension, min);
    } else if (coordinate >= max) {
      // Constrain object dimension to max

      // Avoid changing object position when resizing from left or top
      if (isLeftTop) {
        if (isWidth) {
          object.set('left', object.left + (object.width - max) * scale);
        } else {
          object.set('top', object.top + (object.height - max) * scale);
        }
      }
      object.set(dimension, max);
    } else {
      // Set flag to indicate user set this dimension explicity
      if (isWidth) {
        fabric.controlsUtils.changeWidth(eventData, transform, x, y);
      } else {
        changeHeight(eventData, transform, x, y);
      }
    }

    return prevDimension !== object[dimension];
  }
}

export default ResizeControl;
